
import { useReviews } from "@/hooks/useReviews";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Skeleton } from "@/components/ui/skeleton";
import { Star } from "lucide-react";
import { formatUserDisplayName, getInitials } from "@/utils/userNameUtils";

interface ProviderReviewsTabProps {
  providerId: string;
}

const ProviderReviewsTab = ({ providerId }: ProviderReviewsTabProps) => {
  const { data: reviews, isLoading } = useReviews(providerId);

  const getAvatarColor = (userId: string | null) => {
    if (!userId) return 'bg-gray-100 text-gray-600';
    const colors = [
      'bg-blue-100 text-blue-600',
      'bg-green-100 text-green-600',
      'bg-purple-100 text-purple-600',
      'bg-pink-100 text-pink-600',
      'bg-yellow-100 text-yellow-600',
      'bg-indigo-100 text-indigo-600',
      'bg-orange-100 text-orange-600',
      'bg-teal-100 text-teal-600',
    ];
    const index = userId.charCodeAt(0) % colors.length;
    return colors[index];
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  };

  const renderStars = (rating: number) => {
    return Array.from({ length: 5 }, (_, i) => (
      <Star
        key={i}
        className={`h-4 w-4 ${
          i < rating ? 'fill-yellow-400 text-yellow-400' : 'fill-gray-200 text-gray-200'
        }`}
      />
    ));
  };

  const calculateAverageRating = () => {
    if (!reviews || reviews.length === 0) return 0;
    const sum = reviews.reduce((acc, review) => acc + review.rating, 0);
    return (sum / reviews.length).toFixed(1);
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Reviews</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="flex space-x-4">
                <Skeleton className="w-12 h-12 rounded-full" />
                <div className="flex-1 space-y-2">
                  <Skeleton className="h-4 w-1/4" />
                  <Skeleton className="h-4 w-3/4" />
                  <Skeleton className="h-4 w-1/2" />
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Customer Reviews</CardTitle>
        {reviews && reviews.length > 0 && (
          <div className="flex items-center space-x-2">
            {renderStars(Math.round(Number(calculateAverageRating())))}
            <span className="text-2xl font-bold">{calculateAverageRating()}</span>
            <span className="text-gray-500">
              ({reviews.length} review{reviews.length !== 1 ? 's' : ''})
            </span>
          </div>
        )}
      </CardHeader>
      <CardContent>
        {reviews && reviews.length > 0 ? (
          <div className="space-y-6">
            {reviews.map((review) => (
              <div key={review.id} className="flex space-x-4 border-b border-gray-100 pb-4 last:border-b-0">
                <Avatar className="w-12 h-12">
                  <AvatarFallback className={`font-semibold ${getAvatarColor(review.user_id)}`}>
                    {getInitials(
                      review.profiles?.first_name || null,
                      review.profiles?.last_name || null,
                      review.user_id
                    )}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-semibold text-gray-900">
                      {formatUserDisplayName(
                        review.profiles?.first_name || null,
                        review.profiles?.last_name || null,
                        review.user_id
                      )}
                    </span>
                    <span className="text-gray-500 text-sm">
                      {formatDate(review.created_at)}
                    </span>
                  </div>
                  <div className="flex items-center space-x-1 mb-2">
                    {renderStars(review.rating)}
                  </div>
                  {review.comment && (
                    <p className="text-gray-700 leading-relaxed">{review.comment}</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center text-gray-500 py-8">
            <Star className="h-12 w-12 mx-auto mb-4 text-gray-300" />
            <p>No reviews yet for this provider.</p>
            <p className="text-sm mt-2">Reviews from customers will appear here.</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default ProviderReviewsTab;
